import { useEffect, useState, useRef, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * Performance-optimized space background with cycling images
 * 
 * Optimizations:
 * - Uses requestAnimationFrame for smoother, more efficient animation cycles
 * - Medium intervals between transitions (2 minutes default)
 * - Minimal transition animations with short duration
 * - Preloads only the necessary images
 * - Memoized component to prevent unnecessary re-renders
 * - Proper cleanup of animation frames and timers
 */

// Use regular background images since the low-quality versions don't exist
const backgrounds = [
  '/backgrounds/background-1.jpeg',
  '/backgrounds/background-3.jpg',
  '/backgrounds/background-5.jpg',
];

interface SpaceBackgroundProps {
  interval?: number; // Time in ms between background changes
  opacity?: number; // Background opacity (0-1)
  selectedBackground?: string; // Optional specific background to show
  disabled?: boolean; // Optional flag to completely disable the background
}

function SpaceBackgroundComponent({ 
  interval = 300000, // 5 minutes by default for cycling backgrounds
  opacity = 0.85,    // Default opacity at 85%
  selectedBackground,
  disabled = false
}: SpaceBackgroundProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [imagesPreloaded, setImagesPreloaded] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const requestRef = useRef<number | null>(null);
  
  // Shorter transition duration for better performance
  const transitionDuration = 0.2;

  // If disabled, return early with just a simple dark background
  if (disabled) {
    return <div className="fixed inset-0 -z-10 bg-black/90" />;
  }
  
  // Preload only the images we'll actually use
  useEffect(() => {
    let isMounted = true;
    
    const preloadImages = async () => {
      // Only preload the images we'll actually use
      const imagesToPreload = selectedBackground ? [selectedBackground] : backgrounds;
      
      const imagePromises = imagesToPreload.map((src) => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.src = src;
          img.onload = resolve;
          img.onerror = reject;
        });
      });
      
      try {
        await Promise.all(imagePromises);
        if (isMounted) {
          setImagesPreloaded(true);
        }
      } catch (error) {
        // Continue even if some images fail to load
        if (isMounted) {
          setImagesPreloaded(true);
        }
      }
    };
    
    preloadImages();
    
    return () => {
      isMounted = false;
    };
  }, [selectedBackground]);

  // Set up the background cycling with requestAnimationFrame for better performance
  useEffect(() => {
    if (!imagesPreloaded) return;
    
    // If a specific background is selected, don't cycle
    if (selectedBackground) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
        requestRef.current = null;
      }
      return;
    }
    
    // Start with a random background
    const randomIndex = Math.floor(Math.random() * backgrounds.length);
    setCurrentIndex(randomIndex);

    // Only set up interval if there's no specific background
    if (!selectedBackground && interval > 0) {
      // Using both setInterval (for timing) and requestAnimationFrame (for rendering)
      let lastTimestamp = performance.now();
      let elapsed = 0;
      
      // Function to switch backgrounds using requestAnimationFrame
      const nextBackgroundFrame = (timestamp: number) => {
        elapsed += timestamp - lastTimestamp;
        lastTimestamp = timestamp;
        
        // If it's time to change the background
        if (elapsed >= interval) {
          setIsTransitioning(true);
          setCurrentIndex((prevIndex) => (prevIndex + 1) % backgrounds.length);
          elapsed = 0;
        }
        
        // Continue the animation loop
        requestRef.current = requestAnimationFrame(nextBackgroundFrame);
      };
      
      // Start the animation frame loop
      requestRef.current = requestAnimationFrame((timestamp) => {
        lastTimestamp = timestamp;
        requestRef.current = requestAnimationFrame(nextBackgroundFrame);
      });
    }

    // Clean up all timers on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
        requestRef.current = null;
      }
    };
  }, [interval, imagesPreloaded, selectedBackground]);

  // Handle transition completion
  const handleAnimationComplete = () => {
    setIsTransitioning(false);
  };

  // Show a simplified loading placeholder if images aren't preloaded yet
  if (!imagesPreloaded) {
    return <div className="fixed inset-0 -z-10 bg-black" />;
  }

  // Use the background image
  const backgroundImage = selectedBackground || backgrounds[currentIndex];
  
  // Use a static background when no interval is provided
  if (!interval || interval <= 0) {
    return (
      <div 
        className="fixed inset-0 -z-10 bg-cover bg-center bg-no-repeat"
        style={{ 
          backgroundImage: `url(${backgroundImage})`,
          opacity: opacity * 0.6, // Reduce base opacity to 60% of original
          filter: 'blur(2px)' // Increase blur for more fading
        }}
      >
        {/* Much darker overlay for very subtle background */}
        <div className="absolute inset-0 bg-black/70" />
      </div>
    );
  }

  // Optimized version with minimal animations
  return (
    <div className="fixed inset-0 -z-10 overflow-hidden">
      <AnimatePresence mode="wait">
        <motion.div
          key={selectedBackground || currentIndex}
          initial={{ opacity: 0 }}
          animate={{ opacity: opacity * 0.6 }} // Reduce base opacity to 60% of original
          exit={{ opacity: 0 }}
          transition={{ duration: transitionDuration }}
          onAnimationComplete={handleAnimationComplete}
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ 
            backgroundImage: `url(${backgroundImage})`,
            willChange: "opacity",
            filter: 'blur(2px)' // Increase blur for more fading
          }}
        />
      </AnimatePresence>
      
      {/* Much darker overlay to make background very subtle */}
      <div className="absolute inset-0 bg-black/70 z-10" />
    </div>
  );
}

// Memoize the component to prevent unnecessary re-renders
export default memo(SpaceBackgroundComponent); 